import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Target } from 'lucide-react';
import { SpotCheckSummary } from './SpotCheckSummary';

const STYLES = ['flat', 'hobbled', 'relaxed', 'cascade', 'aventura', 'pleated'];
const abs = (u) => (!u ? null : u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
const ACTION = { approve: 'Approved', redo: 'Redo', keep: 'Kept existing', flag_wrong: 'Flagged QC wrong', spot_ok: 'Spot-check OK' };

/* Manual decisions vs automated QC — Keep existing + Borderline are logged but excluded from the % */
export const QcAccuracyLog = ({ api, headers, refreshKey }) => {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => axios.get(`${api}/qc/accuracy`, { headers }).then(({ data }) => setD(data)).catch(() => {}), [api, headers]);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!d) return null;
  return (
    <div className="rounded-md bg-white border border-neutral-200 p-2 text-[11px]" data-testid="qc-accuracy-log">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="flex items-center gap-1 font-semibold text-neutral-900"><Target className="w-3.5 h-3.5" />QC accuracy</span>
        {STYLES.map(s => {
          const v = d.per_style[s] || {};
          return (
            <span key={s} className="text-neutral-600" data-testid={`qc-accuracy-${s}`}>
              <span className="capitalize">{s}</span>: {v.agreement_pct == null ? '—' : `${v.agreement_pct}%`}
              <span className="text-neutral-400"> ({(v.agree || 0) + (v.disagree || 0)} judged{v.excluded ? `, ${v.excluded} excl.` : ''}{(v.spot_agree || v.spot_disagree) ? ` · spot ${v.spot_agree || 0}✓ ${v.spot_disagree || 0}✗` : ''})</span>
            </span>
          );
        })}
        <span className="ml-auto"><SpotCheckSummary api={api} headers={headers} refreshKey={refreshKey} /></span>
        <button className="text-blue-700 hover:underline" onClick={() => setOpen(o => !o)} data-testid="qc-accuracy-toggle">
          {open ? 'Hide' : 'Show'} disagreements ({d.disagreements.length})
        </button>
      </div>
      {open && (
        <div className="mt-2 max-h-56 overflow-y-auto divide-y divide-neutral-100" data-testid="qc-disagreements-list">
          {d.disagreements.length === 0 ? <p className="text-neutral-400 py-1">No disagreements yet.</p> : d.disagreements.map(x => (
            <div key={x.id} className="flex items-center gap-2 py-1" data-testid={`qc-disagreement-${x.id}`}>
              <a href={abs(x.url)} target="_blank" rel="noreferrer"><img src={abs(x.url)} alt="" className="w-10 h-10 object-cover rounded" /></a>
              <span className="text-neutral-800 font-medium">{x.color_name}</span>
              <span className="text-neutral-500 capitalize">{x.style}</span>
              <span>QC <b className="uppercase">{x.qc_verdict}</b> → {ACTION[x.action] || x.action}</span>
              {x.note && <span className="text-neutral-500 italic truncate">“{x.note}”</span>}
              <span className="ml-auto text-neutral-400">{(x.at || '').slice(0, 10)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
